import { ApolloQueryResult } from '@apollo/client';
import { InvoiceStatus } from '@prisma/client';
import { selfBillingInvoice } from 'types/graphql';

import { useQuery } from '@redwoodjs/web';

import { GET_SELF_BILLING_INVOICE } from 'src/graphql/queries/getSelfBillingInvoices.query';
import { onError } from 'src/utils/utils';

type UseSelfBillingInvoiceProps = {
    id: string;
};

type UseSelfBillingInvoiceResponse = {
    selfBillingInvoice: selfBillingInvoice['selfBillingInvoice'];
    refetchSelfBillingInvoice: (
        variables?: Partial<GraphQLOperationVariables>
    ) => Promise<ApolloQueryResult<selfBillingInvoice>>;
    isSelfBillingInvoiceProcessing: boolean;
    isSelfBillingInvoiceCreateFailed: boolean;
    isSelfBillingInvoiceReleaseFailed: boolean;
    isSelfBillingInvoiceEmailFailed: boolean;
    isAllowedToAllocatePaymentToSelfBillingInvoice: boolean;
    isLoadingSelfBillingInvoice: boolean;
};

function useSelfBillingInvoice({ id }: UseSelfBillingInvoiceProps): UseSelfBillingInvoiceResponse {
    const {
        loading: isLoadingSelfBillingInvoice,
        data: selfBillingInvoiceData,
        refetch: refetchSelfBillingInvoice,
    } = useQuery<selfBillingInvoice>(GET_SELF_BILLING_INVOICE, {
        fetchPolicy: 'cache-and-network',
        skip: !id,
        onError,
        variables: { id: Number.parseInt(id, 10) },
    });

    const selfBillingInvoice = selfBillingInvoiceData?.selfBillingInvoice;

    const isSelfBillingInvoiceProcessing = selfBillingInvoice?.status === InvoiceStatus.PROCESSING;

    const isSelfBillingInvoiceCreateFailed = selfBillingInvoice?.status === InvoiceStatus.FAILED;

    const isSelfBillingInvoiceReleaseFailed =
        selfBillingInvoice?.status === InvoiceStatus.PARTIALLY_FAILED &&
        selfBillingInvoice?.sageInvoiceNumber === null;

    const isSelfBillingInvoiceEmailFailed =
        selfBillingInvoice?.status === InvoiceStatus.PARTIALLY_FAILED &&
        selfBillingInvoice?.sageInvoiceNumber !== null;

    const isAllowedToAllocatePaymentToSelfBillingInvoice =
        selfBillingInvoice?.status === InvoiceStatus.PARTIALLY_PAID ||
        selfBillingInvoice?.status === InvoiceStatus.UNPAID;

    const isAllowedToAllocatePaymentToSelfBillingInvoiceWhileOnlyEmailFailed =
        isAllowedToAllocatePaymentToSelfBillingInvoice ||
        (isSelfBillingInvoiceEmailFailed && !isSelfBillingInvoiceReleaseFailed);

    return {
        selfBillingInvoice,
        refetchSelfBillingInvoice,
        isLoadingSelfBillingInvoice,
        isSelfBillingInvoiceProcessing,
        isSelfBillingInvoiceCreateFailed,
        isSelfBillingInvoiceReleaseFailed,
        isSelfBillingInvoiceEmailFailed,
        isAllowedToAllocatePaymentToSelfBillingInvoice:
            isAllowedToAllocatePaymentToSelfBillingInvoice ||
            isAllowedToAllocatePaymentToSelfBillingInvoiceWhileOnlyEmailFailed,
    };
}

export default useSelfBillingInvoice;
